import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, takeUntil } from 'rxjs';
import { IZefixCompanyDTO } from '../../dtos/zefix-company.dto';
import { ZefixService } from '../../services/zefix.service';
import { ZEFIX_UID_PATTERN } from '../../constants/zefix-legal-form-mapping';

@UntilDestroy()
@Component({
    selector: 'zefix-search',
    templateUrl: './zefix-search.component.html',
    styleUrls: ['./zefix-search.component.scss'],
})
export class ZefixSearchComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isActive = false;
    @Output() companySelected = new EventEmitter<IZefixCompanyDTO>();
    @Output() manualEntry = new EventEmitter<void>();

    @ViewChild('searchInputRef') searchInputRef: ElementRef<HTMLInputElement>;

    searchControl = new UntypedFormControl('');

    results: IZefixCompanyDTO[] = [];
    isLoading = false;
    showDropdown = false;
    isUidMode = false;
    apiError = false;
    hasSearched = false;
    activeIndex = -1;

    private searchTrigger$ = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private zefixService: ZefixService,
        private elementRef: ElementRef,
    ) {}

    ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((query: string) => this.onQueryChange(query));

        this.searchTrigger$
            .pipe(
                switchMap((query) => {
                    if (!query || query.length < 3) {
                        return of(null);
                    }
                    this.isLoading = true;
                    this.apiError = false;
                    return this.zefixService.search(query).pipe(
                        catchError(() => {
                            this.apiError = true;
                            this.isLoading = false;
                            return of([]);
                        }),
                    );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((results) => {
                this.isLoading = false;
                if (results === null) {
                    this.showDropdown = false;
                    this.results = [];
                    return;
                }
                this.results = results as IZefixCompanyDTO[];
                this.hasSearched = true;
                this.showDropdown = true;
                this.activeIndex = -1;
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isActive'] && !this.isActive) {
            this.reset();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private onQueryChange(query: string): void {
        const trimmed = query?.trim() ?? '';
        this.isUidMode = ZEFIX_UID_PATTERN.test(trimmed);
        this.apiError = false;

        if (!trimmed || trimmed.length < 3) {
            this.showDropdown = false;
            this.results = [];
            this.hasSearched = false;
            return;
        }

        this.searchTrigger$.next(trimmed);
    }

    onSelect(co: IZefixCompanyDTO): void {
        this.companySelected.emit(co);
        this.reset();
    }

    onManualEntry(): void {
        this.manualEntry.emit();
        this.closeDropdown();
    }

    clearSearch(): void {
        this.searchControl.setValue('');
        this.results = [];
        this.showDropdown = false;
        this.hasSearched = false;
        this.isUidMode = false;
        this.apiError = false;
        this.searchInputRef?.nativeElement.focus();
    }

    closeDropdown(): void {
        this.showDropdown = false;
        this.activeIndex = -1;
    }

    onKeydown(event: KeyboardEvent): void {
        if (!this.showDropdown) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activeIndex = Math.max(this.activeIndex - 1, -1);
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
            event.preventDefault();
            this.onSelect(this.results[this.activeIndex]);
        } else if (event.key === 'Escape') {
            this.closeDropdown();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeDropdown();
        }
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'BEING_CANCELLED': return 'In Liquidation';
            case 'CANCELLED': return 'Gelöscht';
            default: return '';
        }
    }

    isNonActive(status: string): boolean {
        return status === 'BEING_CANCELLED' || status === 'CANCELLED';
    }

    highlightMatch(text: string, query: string): string {
        const q = this.searchControl.value?.trim();
        if (!q || q.length < 2) return text;
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
    }

    private reset(): void {
        this.searchControl.setValue('', { emitEvent: false });
        this.results = [];
        this.showDropdown = false;
        this.hasSearched = false;
        this.isUidMode = false;
        this.isLoading = false;
        this.apiError = false;
        this.activeIndex = -1;
    }

    get hasQuery(): boolean {
        return !!(this.searchControl.value?.trim());
    }
}
